import equal from "fast-deep-equal";
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";

import { flightRecorder } from "@/services/debug/FlightRecorder";
import type { CollectionName, RowOf } from "@/store/collections";
import type { RowChange } from "@/store/EntityStore";
import { store } from "@/store/store";

/**
 * Per watched collection, the keys a written row of it can change the value of; `null` when the row cannot say, which
 * recomputes every key. A collection with no entry recomputes every key on any write to it.
 *
 * Each runs as the write commits, so the row it gets is the one just stored.
 */
export type AffectedKeys = { [K in CollectionName]?: (row: RowOf<K>) => Iterable<string> | null };

/**
 * One value per key, recomputing only the keys the writes since the last run touched.
 *
 * {@link track} hears every write to the watched collections and logs the keys it feeds; {@link run} recomputes those
 * and keeps the rest. A delete, a truncate, a write it cannot attribute, or new inputs recompute everything. Values
 * compare deeply, and the result keeps its identity while no value changes.
 */
export class KeyedComputation<Value> {
  private values: ReadonlyMap<string, Value> = new Map();
  private readonly dirty = new Set<string>();
  /** Starts true: writes before the subscription attached were never logged. */
  private all = true;
  private inputs: readonly unknown[] | null = null;

  public constructor(
    private readonly watched: ReadonlySet<CollectionName>,
    private affected: AffectedKeys,
  ) {}

  /** Replaces the row-to-keys mapping, for a caller whose closure changed. */
  public setAffected(affected: AffectedKeys): void {
    this.affected = affected;
  }

  /** Logs one committed write; wire it to `store.subscribeRowChanges`. */
  public track(change: RowChange<CollectionName>): void {
    if (this.all || !this.watched.has(change.collection)) return;
    if (change.truncated || change.deleted.length > 0) {
      this.all = true;
      return;
    }
    for (const rowKey of change.upserted) {
      const row = store.get(change.collection, rowKey);
      const keys = row === undefined ? null : this.keysOf(change.collection, row);
      if (keys === null) {
        this.all = true;
        return;
      }
      for (const key of keys) this.dirty.add(key);
    }
  }

  /** Forgets the write log, as when the subscription reattaches and may have missed writes. */
  public invalidate(): void {
    this.all = true;
  }

  /**
   * The values for `keys` after the writes logged since the last run.
   *
   * @param keys - The keys to compute; a different list recomputes everything.
   * @param computeKey - One key's value, from the store.
   * @param inputs - Everything else `computeKey` reads; any change recomputes everything.
   */
  public run(
    keys: readonly string[],
    computeKey: (key: string) => Value,
    inputs: readonly unknown[],
  ): ReadonlyMap<string, Value> {
    const allInputs = [keys.join(","), ...inputs];
    const incremental = !this.all && sameInputs(this.inputs, allInputs);
    this.values = incremental ? this.recomputeDirty(computeKey) : this.recomputeAll(keys, computeKey);
    this.dirty.clear();
    this.all = false;
    this.inputs = allInputs;
    return this.values;
  }

  private keysOf(collection: CollectionName, row: RowOf<CollectionName>): Iterable<string> | null {
    // The mapped type pairs each entry with its own row type; TypeScript cannot follow that through a union key.
    const keysOf = this.affected[collection] as ((row: RowOf<CollectionName>) => Iterable<string> | null) | undefined;
    return keysOf ? keysOf(row) : null;
  }

  /** Recomputes only the dirty keys the result holds, copying the map on the first value that changed. */
  private recomputeDirty(computeKey: (key: string) => Value): ReadonlyMap<string, Value> {
    let copy: Map<string, Value> | null = null;
    for (const key of this.dirty) {
      if (!this.values.has(key)) continue;
      const value = computeKey(key);
      if (equal(value, this.values.get(key))) continue;
      copy ??= new Map(this.values);
      copy.set(key, value);
    }
    return copy ?? this.values;
  }

  /** Recomputes every key, keeping the previous map when no value changed. */
  private recomputeAll(keys: readonly string[], computeKey: (key: string) => Value): ReadonlyMap<string, Value> {
    const next = new Map(keys.map((key) => [key, computeKey(key)]));
    return sameValues(this.values, next) ? this.values : next;
  }
}

/**
 * {@link KeyedComputation} as a hook: one value per key, re-run on writes to `collections` and recomputing only the
 * keys they touched.
 *
 * The per-key alternative to `useCollectionsCompute` for a compute that is a map over many independent keys, such as
 * one count per driver feed: a write landing on one feed costs one key's compute, not all of them. Consumers memoized
 * on the result skip while no value changes.
 *
 * @param name - The compute's label in the flight recorder.
 * @param collections - Every collection `computeKey` reads; a write to any other is not seen.
 * @param keys - The keys to compute, in the order the result lists them.
 * @param computeKey - One key's value, from the store.
 * @param affectedKeys - Which keys a written row feeds.
 * @param deps - Everything else `computeKey` reads, such as a clock; compared element by element.
 */
export function useKeyedCompute<Value>(
  name: string,
  collections: readonly CollectionName[],
  keys: readonly string[],
  computeKey: (key: string) => Value,
  affectedKeys: AffectedKeys,
  deps: readonly unknown[],
): ReadonlyMap<string, Value> {
  const collectionsKey = collections.join(",");
  const computationRef = useRef<KeyedComputation<Value> | null>(null);
  computationRef.current ??= new KeyedComputation<Value>(new Set(collections), affectedKeys);
  const computation = computationRef.current;
  computation.setAffected(affectedKeys);

  // One computation per mount, watching the collections it was created with; callers pass a constant list.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the computation is fixed for the mount's lifetime
  useEffect(() => {
    computation.invalidate();
    return store.subscribeRowChanges((change) => computation.track(change));
  }, []);

  // Same re-render signal as `useCollectionsCompute`: the version sum of the watched collections.
  const subscribe = useCallback((listener: () => void) => store.subscribeWrites(listener), []);
  // biome-ignore lint/correctness/useExhaustiveDependencies: collectionsKey is the stable identity of the collections list
  const version = useSyncExternalStore(
    subscribe,
    useCallback(() => store.versionOf(collections), [collectionsKey]),
  );

  const keysKey = keys.join(",");
  const previousRef = useRef<ReadonlyMap<string, Value> | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: version is the change signal; keysKey is the keys' identity
  return useMemo(() => {
    const startedAt = performance.now();
    const values = computation.run(keys, computeKey, deps);
    flightRecorder.recordCompute(name, startedAt, performance.now(), values !== previousRef.current);
    previousRef.current = values;
    return values;
  }, [version, keysKey, name, ...deps]);
}

function sameValues<Value>(a: ReadonlyMap<string, Value>, b: ReadonlyMap<string, Value>): boolean {
  if (a.size !== b.size) return false;
  for (const [key, value] of b) {
    if (!a.has(key) || !equal(a.get(key), value)) return false;
  }
  return true;
}

function sameInputs(a: readonly unknown[] | null, b: readonly unknown[]): boolean {
  return a !== null && a.length === b.length && a.every((value, index) => Object.is(value, b[index]));
}
